import { useEffect, useMemo, useState } from 'react'
import {
  MRT_PaginationState,
  type MRT_ColumnDef,
  MRT_SortingState,
  MRT_VisibilityState,
  MRT_ColumnFiltersState,
} from 'mantine-react-table'
import { ProjectsGrade, ResidentialListItem } from '@/shared/api/topli/TopliApi'
import { Checkbox } from '@/shared/ui/Checkbox/Checkbox'
import CompanyCell from '../ui/ResidentialTable/CompanyCell'
import { useResidentialsByPage } from '@/entities/Residential'
import { Badge, ActionIcon as MantineActionButton, Text } from '@mantine/core'
import { Camera, CameraOff, LinkIcon, MapPin } from 'lucide-react'
import { BuildingsCell } from '../ui/ResidentialTable/BuildingsCell'
import { Link } from 'react-router-dom'
import { navigationRoutes } from '@/shared/routes/navigationRoutes'
import { useTranslation } from 'react-i18next'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { findLabelByValue, formatDateWithoutTime, getCurrentTranslation } from '@/shared/lib/utils'
import { useTableFilters } from '@/shared/hooks/useTableFilters'
import { useDebounce } from '@/shared/lib/useDebounce'
import { TruncatedTextCell } from '@/shared/ui/TruncatedTextCellProps'
import { useTranslatedEnums } from '@/shared/helpers/apiEnumTypesOptions'
import { digitsOnlyFilterProps } from '@/shared/lib/tableFilterUtils'

export const useResidentialTable = (
  onOpenFullDescription: (row: ResidentialListItem) => void,
  onOpenPhotos: (row: ResidentialListItem) => void,
  onOpenMapModal: (row: ResidentialListItem) => void,
  onOpenVideoModal: (row: ResidentialListItem) => void,
) => {
  const { language } = useLanguageStore()
  const { t } = useTranslation(['residential'])
  const { projectGradeOptions } = useTranslatedEnums()

  const [search, setSearch] = useState('')
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>({
    sn_hash: false,
  })
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const { getBoolean, getMultiSelectIds, getTextFilter } = useTableFilters(columnFilters)

  const memoizedSorting = useMemo(() => {
    let orderBy = ''
    if (!sorting.length) return null
    sorting.forEach(item => (item.desc ? (orderBy = `-${item.id}`) : (orderBy = `${item.id}`)))
    return orderBy
  }, [sorting])

  const checkedManualFilter = getBoolean('checked_manual')
  const selectedGradeIds = getMultiSelectIds('grade')
  const activityFilter = getBoolean('activity')
  const internationalHomepageFilter = getBoolean('international_homepage')
  const localHomepageFilter = getBoolean('local_homepage')
  const debouncedSearchById = useDebounce(getTextFilter('id') || '', 500)
  const debouncedSearch = useDebounce(search, 500)

  const {
    data: residentials,
    isLoading: isLoadingResidentials,
    isError: isErrorResidentials,
    isFetching: isFetchingResidentials,
  } = useResidentialsByPage({
    sorting: memoizedSorting,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    search: debouncedSearch,
    language,
    checked_manual: checkedManualFilter,
    grades: selectedGradeIds,
    activity: activityFilter,
    international_homepage: internationalHomepageFilter,
    local_homepage: localHomepageFilter,
    searchById: debouncedSearchById,
  })

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [debouncedSearch, columnFilters])

  const fetchedResidentials = useMemo(() => residentials?.data ?? [], [residentials])
  const totalRowCount = residentials?.total ?? 0

  const handleSearchChange = (value: string) => {
    setSearch(value)
  }

  const columns = useMemo<MRT_ColumnDef<ResidentialListItem>[]>(
    () => [
      {
        accessorKey: 'id',
        filterVariant: 'text',
        ...digitsOnlyFilterProps(),
        header: 'ID',
        size: 100,
      },
      {
        accessorKey: 'sn_hash',
        enableColumnFilter: false,
        header: 'Hash',
        size: 300,
        enableSorting: false,
        Cell: ({ row }) => `${row.original.sn_hash}, ${row.original.hn_hash}`,
      },
      {
        accessorKey: 'name',
        enableColumnFilter: false,
        header: t('residential:name'),
        Cell: ({ row }) => (
          <Link
            to={navigationRoutes.residentialById(String(row.original.id))}
            className='text-sm font-medium text-blue hover:underline underline-offset-4'
          >
            {getCurrentTranslation(row.original, language, 'name')}
          </Link>
        ),
      },
      {
        accessorKey: 'photos',
        enableColumnFilter: false,
        header: t('residential:photos'),
        enableSorting: false,
        Cell: ({ row }) =>
          row.original.photos ? (
            <MantineActionButton variant='light' onClick={() => onOpenPhotos(row.original)}>
              <Camera />
            </MantineActionButton>
          ) : (
            <CameraOff color='#c5cad0' />
          ),
      },
      {
        accessorKey: 'description',
        enableColumnFilter: false,
        header: t('residential:description'),
        size: 380,
        Cell: ({ row }) => {
          const currentDescription = getCurrentTranslation(row.original, language, 'description')
          return (
            <TruncatedTextCell
              text={currentDescription}
              onShowMore={() => onOpenFullDescription(row.original)}
            />
          )
        },
      },
      {
        accessorKey: 'buildings',
        enableColumnFilter: false,
        header: t('residential:buildings'),
        size: 280,
        Cell: ({ row }) => <BuildingsCell buildings={row.original.buildings} />,
      },
      {
        accessorKey: 'delivery_date',
        enableColumnFilter: false,
        size: 300,
        header: t('residential:delivery_date'),
        Cell: ({ row }) => (
          <div>{formatDateWithoutTime(row.original.delivery_date || null, language)}</div>
        ),
      },
      {
        accessorKey: 'fee',
        enableColumnFilter: false,
        size: 280,
        header: `${t('residential:commission_for_international_clients')},%`,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.fee != null ? row.original.fee : '–'}
          </Text>
        ),
      },
      {
        accessorKey: 'fee_local',
        enableColumnFilter: false,
        size: 280,
        header: `${t('residential:commission_for_local_clients')},%`,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.fee != null ? row.original.fee_local : '–'}
          </Text>
        ),
      },
      {
        accessorKey: 'agent_fee',
        enableColumnFilter: false,
        size: 280,
        header: `${t('residential:agent_commission_for_international_clients')},%`,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.agent_fee != null ? row.original.agent_fee : '–'}
          </Text>
        ),
      },
      {
        accessorKey: 'agent_fee_local',
        enableColumnFilter: false,
        size: 280,
        header: `${t('residential:agent_commission_for_local_clients')},%`,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.agent_fee_local != null ? row.original.agent_fee_local : '–'}
          </Text>
        ),
      },
      {
        accessorKey: 'title_deed',
        enableColumnFilter: false,
        size: 280,
        header: `${t('residential:title_deed')},%`,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.title_deed != null ? row.original.title_deed : '–'}
          </Text>
        ),
      },
      {
        accessorKey: 'vat',
        enableColumnFilter: false,
        size: 280,
        header: `${t('residential:vat')},%`,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.vat != null ? row.original.vat : '–'}
          </Text>
        ),
      },
      {
        accessorKey: 'downpayment',
        enableColumnFilter: false,
        size: 280,
        header: `${t('residential:downpayment')},%`,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.downpayment != null ? row.original.downpayment : '–'}
          </Text>
        ),
      },
      {
        accessorKey: 'duration',
        enableColumnFilter: false,
        size: 280,
        header: `${t('residential:duration_month')}`,
        Cell: ({ row }) => (
          <Text
            lineClamp={4}
            classNames={{
              root: 'flex flex-col w-full mb-6 text-sm font-body',
            }}
          >
            {row.original.duration != null ? row.original.duration : '–'}
          </Text>
        ),
      },
      {
        accessorKey: 'company',
        enableColumnFilter: false,
        header: t('residential:company'),
        Cell: ({ row }) => <CompanyCell company={row.original.company} />,
      },
      {
        accessorKey: 'amenities',
        enableColumnFilter: false,
        header: t('residential:amenities'),
        size: 300,
        Cell: ({ row }) => (
          <div className='flex flex-row flex-wrap gap-1 w-full'>
            {row.original.amenities.length > 0
              ? row.original.amenities.map(amenity => (
                  <Badge key={amenity.id} size='sm' color='gold'>
                    {getCurrentTranslation(amenity, language, 'name')}
                  </Badge>
                ))
              : '–'}
          </div>
        ),
      },
      {
        accessorKey: 'grade',
        header: t('common:library_items.project_grade.sing'),
        filterFn: 'arrIncludesSome',
        filterVariant: 'multi-select',
        mantineFilterMultiSelectProps: {
          data: projectGradeOptions.map(opt => ({
            label: opt.label,
            value: opt.value,
          })),
        },
        Cell: ({ row }) => {
          return row.original?.grade ? (
            <Badge
              size='sm'
              c='#000'
              bg={row.original?.grade === ProjectsGrade.Gold ? '#EFBF04' : '#CE8946'}
            >
              {findLabelByValue(projectGradeOptions, row.original.grade)}
            </Badge>
          ) : (
            '–'
          )
        },
      },
      {
        accessorKey: 'video_url',
        enableColumnFilter: false,
        header: t('residential:webinar_video'),
        enableSorting: false,
        Cell: ({ row }) =>
          row.original.video_url ? (
            <button
              type='button'
              className='bg-transparent border-none text-blue px-0'
              onClick={() => onOpenVideoModal(row.original)}
            >
              {t('common:openLink')}
            </button>
          ) : (
            '-'
          ),
      },
      {
        accessorKey: 'project_url',
        enableColumnFilter: false,
        header: t('residential:project_url'),
        Cell: ({ row }) =>
          row.original.project_url && (
            <a href={row.original.project_url} className='text-blue underline' target='_blank'>
              <LinkIcon />
            </a>
          ),
      },
      {
        accessorKey: 'address',
        enableColumnFilter: false,
        header: t('residential:address'),
        size: 320,
        Cell: ({ row }) => {
          return (
            <div className='flex flex-row items-center w-full space-x-2'>
              <MantineActionButton
                variant='light'
                className='text-blue'
                onClick={() => onOpenMapModal(row.original)}
              >
                <MapPin size={20} />
              </MantineActionButton>
              <div className='text-sm text-black'>{row.original.address.formatted}</div>
            </div>
          )
        },
      },
      {
        accessorKey: 'activity',
        filterVariant: 'checkbox',
        header: t('residential:activity'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.activity} readOnly />,
      },
      {
        accessorKey: 'done',
        enableColumnFilter: false,
        header: t('residential:done'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.done} readOnly />,
      },
      {
        accessorKey: 'hide_price',
        enableColumnFilter: false,
        header: t('residential:hide_price'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.hide_price} readOnly />,
      },
      {
        accessorKey: 'citizenship',
        enableColumnFilter: false,
        header: t('residential:citizenship'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.citizenship} readOnly />,
      },
      {
        accessorKey: 'residency_supported',
        enableColumnFilter: false,
        header: t('residential:residence_permit'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.residency_supported} readOnly />,
      },
      {
        accessorKey: 'local_homepage',
        filterVariant: 'checkbox',
        header: t('residential:local_homepage'),
        size: 300,
        Cell: ({ row }) => <Checkbox checked={!!row.original.local_homepage} readOnly />,
      },
      {
        accessorKey: 'international_homepage',
        filterVariant: 'checkbox',
        header: t('residential:international_homepage'),
        size: 300,
        Cell: ({ row }) => <Checkbox checked={!!row.original.international_homepage} readOnly />,
      },
      {
        accessorKey: 'ai_translations',
        enableColumnFilter: false,
        header: t('residential:machine_translation'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.has_ai_translation} readOnly />,
      },
      {
        accessorKey: 'checked_manual',
        filterVariant: 'checkbox',
        header: t('residential:checked_manually'),
        Cell: ({ row }) => <Checkbox checked={!!row.original.checked_manual} readOnly />,
      },
      {
        accessorKey: 'export_to_projeturk',
        filterVariant: 'checkbox',
        header: t('residential:export_to_projeturk'),
        Cell: ({ row }) => <Checkbox checked={!!row.original?.export_to_projeturk} readOnly />,
      },
      {
        accessorKey: 'pdf',
        header: 'PDF',
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const currentId = row.original.id
          return (
            <a
              href={`${
                import.meta.env.VITE_WEB_APP_URL
              }announcements/residential-projects/${currentId}?download_modal`}
              className='text-sm font-medium text-blue hover:underline underline-offset-4'
              target='_blank'
            >
              pdf
            </a>
          )
        },
      },
    ],
    [onOpenFullDescription, onOpenMapModal, onOpenPhotos, language],
  )
  return {
    pagination,
    fetchedResidentials,
    totalRowCount,
    columns,
    isErrorResidentials,
    isFetchingResidentials,
    isLoadingResidentials,
    sorting,
    setPagination,
    setSorting,
    handleSearchChange,
    columnVisibility,
    setColumnVisibility,
    columnFilters,
    setColumnFilters,
  }
}
